// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, debounce, Observable, of, timer } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import { TimeGroup, UpdateTimeGroupDurationRequest } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { timelineClient } from "app/rpc/client"
import { grpc } from "../../../app/rpc/executor"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { prepareListDaySummaryRequest } from "./daySummaries"
import { RootState } from "../../../app/store"

type UpdateTimeGroupDurationRpc = (request: UpdateTimeGroupDurationRequest) => Observable<TimeGroup>

export const updateTimeGroupDuration = (updateTimeGroupDurationRpc: UpdateTimeGroupDurationRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.updateTimeGroupDuration.match),
      withLatestFrom(state$),
      debounce(() => timer(500)),
      mergeMap(([action, state]) => of(action.payload).pipe(
        mergeMap(updateTimeGroupDurationRpc),
        map(t => t.toObject()),
        mergeMap(t => concat(
          of(TimelineSlice.upsertTimeGroup(t)),
          of(Actions.listDaySummaries(prepareListDaySummaryRequest(state.timeline.activeDate.toString())))
        )),
        catchError(e => handleError(e, action.payload.getTimeGroupId())),
      )),
    )

const updateTimeGroupDurationRpc = (request: UpdateTimeGroupDurationRequest): Observable<TimeGroup> =>
  grpc(() => timelineClient.updateTimeGroupDuration(request, null))

const handleError = (e: unknown, timeGroupId: string): Observable<Action> => {
  const actions: Action[] = [
    TimelineSlice.setFailedTimeGroupId(timeGroupId),
  ]
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      // refresh the timeline since it's likely that we have a stale cache
      actions.push(
        showSnackBar({
          message: t({
            id: "msg_update_time_group_duration_not_found",
            message: "Failed to update time group duration. Could not find the target time group.",
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      actions.push(
        showSnackBar({
          message: t({
            id: "msg_update_time_group_duration_permission_denied",
            message: "Failed to update time group duration. You do not have permission to update the time group.",
          }),
          messageType: "ERROR",
        })
      )
      return concat(actions)
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      // refresh the timeline since it's likely that we have a stale cache
      actions.push(
        showSnackBar({
          message: t({
            id: "msg_update_time_group_duration_failed_precondition",
            message: "Failed to update time group duration. The time group is already posted and can't be changed anymore.",
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(
    showSnackBar({
      message: t({
        id: "msg_update_time_group_duration_failed",
        message: "Failed to update the group duration. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      }),
      messageType: "ERROR",
    })
  )
  return concat(actions)
}

export default updateTimeGroupDuration(updateTimeGroupDurationRpc)
